/**
 * @fileoverview Main application component and state orchestration.
 *
 * This is the root component of the Budget Tracker application that:
 * - Manages authentication state and user session
 * - Orchestrates all custom hooks for data fetching and state
 * - Handles page navigation and routing
 * - Renders appropriate loading/error/auth states
 * - Coordinates data flow between pages and components
 *
 * @description The App component follows a "hooks composition" pattern where
 * multiple custom hooks are composed together to manage different aspects of
 * the application state. This keeps the component logic organized and testable.
 *
 * Application flow:
 * 1. User authenticates via Auth0
 * 2. User profile is synced to database (useAuthUser hook)
 * 3. User selects a budget (or creates one)
 * 4. Budget data is loaded (template, categories, transactions, balances)
 * 5. User navigates between pages (client-side routing via currentPage state)
 *
 * State management strategy:
 * - Auth state: Auth0 context + useAuthUser hook
 * - Data fetching: Individual hooks per domain (categories, transactions, etc.)
 * - UI state: Local component state (currentPage, etc.)
 * - API context: Global token getter and budget context via api service
 *
 * @component
 */

import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter, setBudgetContext } from './services/api';
import { useRealtime } from './contexts/RealtimeContext';

// Custom hooks for data fetching and state management
import { useMonthNavigation } from './hooks/useMonthNavigation';
import { useBudgetTemplate } from './hooks/useBudgetTemplate';
import { useTransactions } from './hooks/useTransactions';
import { useCategories } from './hooks/useCategories';
import { useMonthBalance } from './hooks/useMonthBalance';
import { useCategoryBalances } from './hooks/useCategoryBalances';
import { useAuthUser } from './hooks/useAuthUser';
import { useBudgetHandlers } from './hooks/useBudgetHandlers';
import { useBudgetInfo } from './hooks/useBudgetInfo';

// Layout and authentication components
import Header from './components/Header/Header';
import AuthenticatedApp from './components/Auth/AuthenticatedApp';
import BudgetCreation from './components/BudgetCreation/BudgetCreation';
import Invites from './components/Invites/Invites';
import Profile from './components/Profile/Profile';
import { LoadingState, ErrorState, ProfileCompletionState, BudgetSelectionState } from './components/Auth/AppLoadingStates';

// Page components
import BudgetTrackerPage from './pages/BudgetTrackerPage';
import BudgetTemplatePage from './pages/BudgetTemplatePage';
import CategoriesPage from './pages/CategoriesPage';
import BudgetAllocationPage from './pages/BudgetAllocationPage';
import TransactionTypeReportPage from './pages/TransactionTypeReportPage';
import CategoryReportPage from './pages/CategoryReportPage';
import MonthComparisonReportPage from './pages/MonthComparisonReportPage';
import PendingApprovalPage from './components/Auth/PendingApprovalPage';
import UserApprovalPage from './components/Admin/UserApprovalPage';

/**
 * Main application component.
 * Handles authentication, budget selection, and page routing.
 *
 * @returns {JSX.Element} The rendered application
 */
function App() {
  // Auth0 authentication context
  const { user, getAccessTokenSilently } = useAuth0();

  // Real-time context for multi-user features
  const { joinBudgetRoom, leaveBudgetRoom, isConnected } = useRealtime();

  // Client-side page routing state
  const [currentPage, setCurrentPage] = useState('budget-tracker');

  // Configure API service with token getter for authenticated requests
  useEffect(() => {
    setTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  // User authentication and budget selection state
  const authUser = useAuthUser();
  const { handleBudgetCreated, handleInviteAccepted } = useBudgetHandlers(getAccessTokenSilently, authUser);

  // Update API context and join real-time room when budget changes
  // Also re-join when socket connects (isConnected changes to true)
  useEffect(() => {
    if (authUser.selectedBudget) {
      setBudgetContext(authUser.selectedBudget.budgetKey);

      // Join the budget's real-time room for live updates
      // This also re-runs when isConnected becomes true
      if (isConnected) {
        console.log('[App] Joining budget room:', authUser.selectedBudget.budgetKey);
        joinBudgetRoom(authUser.selectedBudget.budgetKey);
      }

      // Only navigate to budget-tracker if we're not already on a valid page
      // This prevents unwanted navigation when budget data is refreshed
      if (currentPage !== 'budget-tracker' &&
          currentPage !== 'profile' &&
          currentPage !== 'budget-template' &&
          currentPage !== 'categories' &&
          currentPage !== 'budget-allocation' &&
          currentPage !== 'invites' &&
          currentPage !== 'create-budget' &&
          currentPage !== 'report-transaction-type' &&
          currentPage !== 'report-category' &&
          currentPage !== 'report-month-comparison' &&
          currentPage !== 'admin-users') {
        setCurrentPage('budget-tracker');
      }
    }

    // Cleanup: leave budget room when component unmounts or budget changes
    return () => {
      if (authUser.selectedBudget) {
        leaveBudgetRoom();
      }
    };
  }, [authUser.selectedBudget, currentPage, joinBudgetRoom, leaveBudgetRoom, isConnected]);

  // Budget data hooks (categories, template, info)
  // Pass userKey for realtime event filtering to prevent own actions from being duplicated
  const categoryHook = useCategories(authUser.userKey);
  const budgetHook = useBudgetTemplate(null, authUser.userKey);
  const budgetInfo = useBudgetInfo(authUser.selectedBudget);

  // Month navigation and balance hooks
  const monthNav = useMonthNavigation(budgetInfo?.startMonth, budgetInfo?.ceaseDate);
  const balanceHook = useMonthBalance(monthNav.selectedMonth);
  const categoryBalancesHook = useCategoryBalances(monthNav.selectedMonth);

  // Transaction data hook (depends on month, template, and categories)
  // Pass userKey for realtime event filtering to prevent own actions from being duplicated
  // Pass category balances and allocation settings for fill-negative-first feature
  // Allocation settings come from budgetHook (per-month template settings)
  const transactionHook = useTransactions(
    monthNav.selectedMonth,
    budgetHook.budgetTemplate,
    categoryHook.categories,
    authUser.userName,
    budgetInfo?.singleBankMode || false,
    authUser.userKey,
    categoryBalancesHook.categoryBalances,
    budgetHook.fillNegativeFirst || false,
    budgetHook.allowOverTarget || false
  );

  // Reload budget template whenever navigating to the template page
  useEffect(() => {
    if (currentPage === 'budget-template' && budgetHook.reloadBudgetTemplate) {
      budgetHook.reloadBudgetTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Refresh balances whenever transactions change
  useEffect(() => {
    if (balanceHook.refreshBalance && categoryBalancesHook.refreshCategoryBalances) {
      balanceHook.refreshBalance();
      categoryBalancesHook.refreshCategoryBalances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionHook.transactions]);

  // Render loading/error/auth states before main app
  if (authUser.isLoading) return <LoadingState />;
  if (authUser.error) return <ErrorState error={authUser.error} />;

  // Block access for users awaiting or rejected from approval
  if (authUser.needsApproval || authUser.isRejected) {
    return (
      <PendingApprovalPage
        isRejected={authUser.isRejected}
        userEmail={user?.email}
      />
    );
  }

  if (authUser.needsProfileCompletion) {
    return <ProfileCompletionState onComplete={authUser.refreshUser} getAccessTokenSilently={getAccessTokenSilently} />;
  }
  // Only show budget selection when user has NO budgets (not just when selectedBudget is null)
  // Don't show until initial load completes to prevent flash during state updates
  if (authUser.hasLoadedOnce && !authUser.isLoading && authUser.budgets.length === 0) {
    return (
      <BudgetSelectionState
        userEmail={user?.email}
        onBudgetCreated={handleBudgetCreated}
        onInviteAccepted={handleInviteAccepted}
        getAccessTokenSilently={getAccessTokenSilently}
      />
    );
  }
  // Keep showing loading if:
  // 1. Initial sync hasn't completed yet
  // 2. User has budgets but selectedBudget isn't set yet
  if (!authUser.hasLoadedOnce || (authUser.budgets.length > 0 && !authUser.selectedBudget)) {
    return <LoadingState />;
  }

  // User's role in the selected budget (owner, elevated_member, or member)
  const userRole = authUser.selectedBudget.role;

  // Render main authenticated application
  return (
    <AuthenticatedApp>
      <div style={{ fontFamily: 'Arial' }}>
        {/* Global header with navigation and budget selector */}
        <Header
          budgetName={authUser.selectedBudget.budgetName}
          userEmail={user?.email}
          userName={authUser.userName}
          onNavigate={setCurrentPage}
          currentPage={currentPage}
          userRole={userRole}
          budgets={authUser.budgets}
          selectedBudget={authUser.selectedBudget}
          onBudgetChange={authUser.setSelectedBudget}
          currentUserId={authUser.userKey}
          isAdmin={authUser.isAdmin}
        />

        {/* Client-side page routing - render appropriate page based on currentPage state */}
        {currentPage === 'budget-tracker' && (
          <BudgetTrackerPage
            monthNav={monthNav}
            balanceHook={balanceHook}
            transactionHook={transactionHook}
            categoryHook={categoryHook}
            categoryBalancesHook={categoryBalancesHook}
            budgetInfo={budgetInfo}
            budgetHook={budgetHook}
            userRole={userRole}
            userKey={authUser.userKey}
            onNavigateToCategories={() => setCurrentPage('categories')}
          />
        )}

        {currentPage === 'budget-template' && (
          <BudgetTemplatePage
            categoryHook={categoryHook}
            budgetInfo={budgetInfo}
            userRole={userRole}
            onBack={() => setCurrentPage('budget-tracker')}
            onNavigateToCategories={() => setCurrentPage('categories')}
          />
        )}

        {currentPage === 'categories' && (
          <CategoriesPage
            categoryHook={categoryHook}
            budgetInfo={budgetInfo}
            userRole={userRole}
            onBack={() => setCurrentPage('budget-tracker')}
            onCategoryBalancesRefresh={categoryBalancesHook.refreshCategoryBalances}
          />
        )}

        {currentPage === 'budget-allocation' && (
          <BudgetAllocationPage
            categoryHook={categoryHook}
            onBack={() => setCurrentPage('budget-tracker')}
            onNavigateToCategories={() => setCurrentPage('categories')}
            onNavigateToBudgetTemplate={() => setCurrentPage('budget-template')}
          />
        )}

        {currentPage === 'profile' && (
          <Profile
            userName={authUser.userName}
            userEmail={user?.email}
            user={user}
            getAccessTokenSilently={getAccessTokenSilently}
            authUser={authUser}
            onBudgetDeleted={async (deletedBudgetKey) => {
              // Refresh user to get updated budget list
              await authUser.refreshUser();

              // After refresh, if there are no more budgets available, we need to clear the page
              // so the App's conditional rendering shows BudgetSelectionState
              // The App will check for !authUser.selectedBudget and show budget creation
              // We just need to not be on a specific page
            }}
          />
        )}

        {currentPage === 'create-budget' && (
          <BudgetCreation
            onBudgetCreated={async (budgetData) => {
              await handleBudgetCreated(budgetData);
              setCurrentPage('budget-tracker');
            }}
            onCancel={() => setCurrentPage('budget-tracker')}
          />
        )}

        {currentPage === 'invites' && (
          <Invites
            userEmail={user?.email}
            budgets={authUser.budgets}
            onInviteAccepted={handleInviteAccepted}
            getAccessTokenSilently={getAccessTokenSilently}
            userRole={userRole}
          />
        )}

        {currentPage === 'report-transaction-type' && (
          <TransactionTypeReportPage
            transactionHook={transactionHook}
            categoryHook={categoryHook}
            budgetInfo={budgetInfo}
            onBack={() => setCurrentPage('budget-tracker')}
          />
        )}

        {currentPage === 'report-category' && (
          <CategoryReportPage
            transactionHook={transactionHook}
            categoryHook={categoryHook}
            budgetInfo={budgetInfo}
            onBack={() => setCurrentPage('budget-tracker')}
          />
        )}

        {currentPage === 'report-month-comparison' && (
          <MonthComparisonReportPage
            budgetInfo={budgetInfo}
            onBack={() => setCurrentPage('budget-tracker')}
          />
        )}

        {currentPage === 'admin-users' && authUser.isAdmin && (
          <UserApprovalPage
            onBack={() => setCurrentPage('budget-tracker')}
          />
        )}
      </div>
    </AuthenticatedApp>
  );
}

export default App;
